"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
export default function BasisOfDesignGetSpecifiedByName() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="basis-of-design-get-specified-by-name" className="bg-[var(--color-logo)] text-accent py-16 md:py-24">
      <div ref={ref} className={`container mx-auto px-4 max-w-4xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h2 className="font-default text-3xl md:text-4xl font-bold mb-6 text-accent">
          How Do You Specify Evermark as Basis of Design?
        </h2>
        
        <p className="font-default text-lg text-accent/80 mb-10 leading-relaxed">
          Evermark Industrial Solutions provides basis-of-design language and a CSI 3-part specification an engineer can adapt into the identification section — Part 1 (general/submittals), Part 2 (products/materials), Part 3 (execution/installation). Citing Evermark as the basis of design sets the quality and material standard the project is held to, while still allowing the engineer to define approved-equal criteria.
        </p>

        <div className="border-l-4 border-primary bg-secondary/40 p-6 md:p-8 rounded-r-lg flex flex-col md:flex-row gap-6 items-start md:items-center justify-between shadow-lg">
          <p className="font-default text-base md:text-lg flex-1 text-accent/90 leading-relaxed">
            Request a submittal package for your spec section and Evermark Industrial Solutions sends the CSI language, data sheets, and standards references ready to adapt. {"{{verify: submittal package delivery details / email-gated download}}"}.
          </p>
          <Button asChild size="lg" className="shrink-0 shadow-md">
            <Link to="/ContactPage">
              Request a Submittal Package
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}